/**
 * Google maps api for internal use. Google apis have interplay which makes interplay with
 * different ui components difficult (address form and map) so centralize them here.
 */
import { Map } from 'immutable';
import scriptjs from 'scriptjs';

import { genAsyncSingleton } from './sharedUtils';
import config from '../igglo_config';
import GoogleMapsTheme from '../styles/GoogleMapsTheme.json';
import trace from './trace';

let extraLibs = null;
const loadMapsSingleton = genAsyncSingleton(() => new Promise(resolve => {
  const queryExtra = (extraLibs.length === 0) ? '' : `&libraries=${extraLibs.join(',')}`;

  scriptjs(
    `https://maps.googleapis.com/maps/api/js?key=${config.gmapsApiKey}&callback=whenGoogleMapsHaveLoaded${queryExtra}`,
    () => resolve(true)
  );
}));

export function setup(withExtraLibs = []) {
  extraLibs = withExtraLibs;
}

export function load() {
  if (!Array.isArray(extraLibs)) {
    throw new Error(`Map setup not done correctly! extraLibs is (${extraLibs}), but it should be an array.`);
  }

  loadMapsSingleton()
    .then(() => trace.log('Maps loaded!'))
    .catch(() => trace.error('Failed to load maps!'));
}

// singleton to hold the currently active map. needed for places api later
let theMap;

const getGeocoder = genAsyncSingleton(
  new Promise(resolve => {
    if (global.google && global.google.maps) {
      resolve(new global.google.maps.Geocoder());
    } else {
      global.pendingGoogleMapsRequests.push(() =>
        resolve(new global.google.maps.Geocoder())
      );
    }
  })
);

export function createLocation(coordinates, longitude) {
  if (longitude !== undefined) {
    return new global.google.maps.LatLng(coordinates, longitude);
  }

  return new global.google.maps.LatLng(coordinates.latitude, coordinates.longitude);
}

export function createMap(canvas, center, zoom, type = 'normal') {
  let mapOptions;
  if (type === 'normal') {
    mapOptions = {
      zoom,
      center,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControlOptions: {
        position: global.google.maps.ControlPosition.RIGHT_CENTER
      }
    };
  } else {
    mapOptions = {
      zoom,
      center,
      clickableIcons: false,
      disableDoubleClickZoom: true,
      draggable: false,
      gestureHandling: 'none',
      keyboardShortcuts: false,
      mapTypeControl: false,
      panControl: false,
      rotateControl: false,
      scaleControl: false,
      scrollWheel: false,
      streetViewControl: false,
      zoomControl: false
    };
  }

  theMap = new global.google.maps.Map(canvas, { ...mapOptions, styles: GoogleMapsTheme });
  return theMap;
}

export function createMarker(map, position, icon, type = 'normal') {
  const markerOptions = { map, position, icon };
  markerOptions.draggable = type === 'normal';

  return new global.google.maps.Marker(markerOptions);
}

export function addMapListener(map, event, listener) {
  global.google.maps.event.addListener(map, event, listener);
}

function parseGoogleLocationResult(criterias, short, result) {
  const outcome = result.address_components.find(component =>
    component.types.some(type => criterias.includes(type))
  );
  if (outcome) {
    return short ? outcome.short_name : outcome.long_name;
  }
  return '';
}

const addressTypes = Map({
  country: { types: ['country'], short: true },
  city: { types: ['locality', 'administrative_area_level_3'], short: false },
  postalCode: { types: ['postal_code'], short: false },
  street: { types: ['route'], short: false },
  streetNumber: { types: ['street_number'], short: false }
});

const parseResult = addressTypes.map(
  settings => parseGoogleLocationResult.bind(undefined, settings.types, settings.short)
).toObject();

function parseAddress(result) {
  const street = parseResult.street(result);
  const streetNumber = parseResult.streetNumber(result);
  const postalCode = parseResult.postalCode(result);
  const city = parseResult.city(result);

  return `${street} ${streetNumber},\n${postalCode} ${city}`;
}

async function geocodeLocationWithRawResult(latLng) {
  const geocoder = await getGeocoder();

  return new Promise((resolve, reject) => {
    geocoder.geocode({ latLng },
      (results, status) => {
        if (status === global.google.maps.GeocoderStatus.OK) {
          if (results && results.length) {
            resolve(results);
          } else {
            reject(new Error('Empty geocoding result'));
          }
        } else {
          reject(new Error('Bad response from geocoding service', results));
        }
      });
  });
}

export function parseLocation(location) {
  return {
    address: parseAddress(location),
    city: parseResult.city(location),
    country: parseResult.country(location)
  };
}

export async function geocodeLocation(location) {
  const result = (await geocodeLocationWithRawResult(location))[0];

  return {
    ...parseLocation(result),
    location
  };
}

export function getCurrentLocation() {
  return new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(resolve, reject);
    } else {
      reject(new Error("Browser doesn't support geoLocation API."));
    }
  })
    .then(it => it.coords);
}

export function geocodeCurrentLocation() {
  return getCurrentLocation()
    .then(createLocation)
    .then(geocodeLocation);
}
